import {bulletinUrlPrefix, racecourseUrlPrefix, warningLogStyle} from "constants/global.vars";
import {ApiService} from "lib/http.service";
import {ServerDate} from "services/misc.functions";
import {jewelryMapping} from "constants/reference.data";
import {bettingTypes, BettingTypesMap} from "features/core/definitions";
import {Hippodrome} from "models/bulletin.models";
import {publishBulletinSnapshot, publishHippodromesMap} from "features/bulletin/bulletin.store";
import {validateHippodrome} from "features/bulletin/lib/models";
import _ from "lodash";

const DailyRaces: any[] = [];
const SpecialRaces: any[] = [];
const BulletinMap: Record<string, any> = {};
const BulletinMapCardID: Record<string, any> = {};
const HippodromesMap: Record<string, any> = {};
const availableHippodromes: any[] = [];
const availableFobHippodromes: any[] = [];
const availableLongtermHippodromes: any[] = [];

let bulletinTimer: any = null;
let isFetchInFlight = false;
let consecutiveFailures = 0;

function isSameValue(a: any, b: any): boolean {
    if (a === b) return true;
    if (a instanceof Date || b instanceof Date) {
        return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
    }
    if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => isSameValue(a[key], b[key]));
}

function mergeHorse(exHorse: any, newHorse: any): boolean {
    let changed = false;
    for (const [key, value] of Object.entries(newHorse)) {
        if (!isSameValue(exHorse[key], value)) {
            exHorse[key] = value;
            changed = true;
        }
    }
    return changed;
}

function mergeOdds(exRun: any, newOdds: any): boolean {
    const exOdds = exRun.event_odds ?? {};
    let changed = Object.keys(newOdds).length !== Object.keys(exOdds).length;

    for (const [oddKey, oddValue] of Object.entries<any>(newOdds)) {
        const exEntry = exOdds[oddKey];
        const before = Number(exEntry?.win_odds);
        const after = Number(oddValue.win_odds);

        oddValue.isUpdated =
            exEntry && Number.isFinite(before) && Number.isFinite(after) && before !== after
                ? after > before
                    ? "up"
                    : "down"
                : "";

        // Vurgu sayacı: aynı yönde art arda gelen değişimlerde sınıf ("up") aynı kaldığı için
        // CSS animasyonu yeniden BAŞLAMIYOR. Bu sayaç düğmenin React anahtarı; her gerçek
        // değişimde düğüm yeniden kurulup animasyon baştan çalışıyor.
        oddValue.oddsRevision = (Number(exEntry?.oddsRevision) || 0) + (oddValue.isUpdated ? 1 : 0);

        // isUpdated karşılaştırmaya DAHİL. Bu alanı bu fonksiyon üretiyor ama ekranda bir CSS
        // sınıfı olarak yaşıyor: karşılaştırmadan çıkarıldığında vurgunun SÖNMESİ
        // ("up"/"down" -> "") tek başına anlık görüntüyü yayınlatmıyordu. Bültende başka
        // hiçbir şey değişmediyse bileşen yeniden çizilmiyor, `.odd-button.up` sınıfı DOM'da
        // kalıyor ve yanıp sönme hiç bitmiyordu.
        if (!exEntry || !isSameValue(exEntry, oddValue)) {
            changed = true;
        }
    }

    exRun.event_odds = newOdds;
    return changed;
}

/**
 * Kim Geçer oranları `event_odds`ta değil `fob_h2h` eşleşmelerinde geliyor; değişim yönü
 * (`isUpdated`) ve vurgu sayacı bu yüzden ayrıca hesaplanıyor. `mergeOdds` ile aynı kural.
 */
function markH2HOddsChanges(exRun: any, newH2H: any, key: string): void {
    const exPairs = exRun?.[key] ?? {};
    for (const [pairKey, pair] of Object.entries<any>(newH2H ?? {})) {
        const exPair = exPairs[pairKey];
        (pair ?? []).forEach((entry: any, index: number) => {
            if (!entry) return;
            const exEntry = exPair?.[index];
            const before = Number(exEntry?.win_odds);
            const after = Number(entry.win_odds);

            entry.isUpdated =
                exEntry && Number.isFinite(before) && Number.isFinite(after) && before !== after
                    ? after > before
                        ? "up"
                        : "down"
                    : "";

            entry.oddsRevision = (Number(exEntry?.oddsRevision) || 0) + (entry.isUpdated ? 1 : 0);
        });
    }
}

function mergeRun(exRun: any, newRun: any): boolean {
    let changed = false;

    for (const [key, value] of Object.entries<any>(newRun)) {
        if (key === "horses") {
            if (!value) {
                if (exRun.horses?.length) changed = true;
                exRun.horses = [];
                continue;
            }
            for (const newHorse of value) {
                const exHorse = exRun.horses.find((el: any) => el.no === newHorse.no);
                if (exHorse) {
                    if (mergeHorse(exHorse, newHorse)) changed = true;
                } else if (newHorse.run_status) {
                    exRun.horses.push(newHorse);
                    if (exRun.horseMap) {
                        exRun.horseMap[String(newHorse.no)] = newHorse;
                    }
                    changed = true;
                }
            }
        } else if (key === "event_odds") {
            if (mergeOdds(exRun, value)) changed = true;
        } else if (key === "fob_h2h" || key === "fob_ww") {
            markH2HOddsChanges(exRun, value, key);
            if (!isSameValue(exRun[key], value)) {
                exRun[key] = value;
                changed = true;
            }
        } else if (!isSameValue(exRun[key], value)) {
            exRun[key] = value;
            changed = true;
        }
    }

    return changed;
}

function mergeRuns(exHippodrome: any, newRuns: any[]): boolean {
    if (!newRuns) {
        const changed = exHippodrome.runs?.length > 0;
        exHippodrome.runs = [];
        return changed;
    }

    let changed = false;
    for (const newRun of newRuns) {
        const exRun = exHippodrome.runs.find((el: any) => el.no === newRun.no);
        if (exRun) {
            if (mergeRun(exRun, newRun)) changed = true;
            continue;
        }

        if (Number(newRun.status) === 0) {
            exHippodrome.runs.push(newRun);
        }
        newRun.horseMap = {};
        for (const horse of newRun.horses ?? []) {
            newRun.horseMap[String(horse.no)] = horse;
        }
        BulletinMap[exHippodrome._id].runsMap[Number(newRun.no)] = newRun;
        changed = true;
    }

    return changed;
}

function mergeWagerDetails(exHippodrome: any, newHippodrome: any): boolean {
    if (newHippodrome.card_id == null) return false;

    if (!newHippodrome.wager_details) {
        const changed = exHippodrome.wager_details?.length > 0;
        exHippodrome.wager_details = [];
        return changed;
    }

    if (!exHippodrome.wager_details) {
        exHippodrome.wager_details = [];
    }

    let changed = false;

    for (const wager of newHippodrome.wager_details) {
        const exBet = exHippodrome.wager_details.find((el: any) => Number(el.type) === Number(wager.type));
        if (!exBet) {
            exHippodrome.wager_details.push(wager);
            changed = true;
            continue;
        }
        for (const key of ["price", "runs"]) {
            if (!isSameValue(exBet[key], wager[key])) {
                exBet[key] = wager[key];
                changed = true;
            }
        }
    }

    for (let ix = exHippodrome.wager_details.length - 1; ix >= 0; ix -= 1) {
        const stillExists = newHippodrome.wager_details.some(
            (el: any) => Number(el.type) === Number(exHippodrome.wager_details[ix].type)
        );
        if (!stillExists) {
            exHippodrome.wager_details.splice(ix, 1);
            changed = true;
        }
    }

    return changed;
}

function mergeHippodrome(exHippodrome: any, newHippodrome: any): boolean {
    let changed = false;

    for (const [key, value] of Object.entries(newHippodrome)) {
        if (key === "nextRun") {
            continue;
        }
        if (key === "wager_details") {
            if (mergeWagerDetails(exHippodrome, newHippodrome)) changed = true;
        } else if (key === "runs") {
            if (mergeRuns(exHippodrome, newHippodrome.runs)) changed = true;
        } else if (!isSameValue(exHippodrome[key], value)) {
            exHippodrome[key] = value;
            changed = true;
        }
    }

    return changed;
}

function prepareHippodrome(hippodrome: any) {
    const {ok} = validateHippodrome(hippodrome);
    if (!ok && !(hippodrome?._id && typeof hippodrome?.hippodrome === "string"
        && typeof hippodrome?.date === "string" && hippodrome?.runs?.length > 0)) {
        throw new Error("Kartın zorunlu alanları (_id / hippodrome / date / runs) eksik.");
    }
    hippodrome.hippodrome = hippodrome.hippodrome.replace(" Hipodromu", "");
    hippodrome.opening_date = new Date(hippodrome.date.split("T")[0] + "T" + hippodrome.opening + ":00");

    const sixStartNo = (...codes: string[]) => {
        const startRun = hippodrome.runs.find((run: any) =>
            (run.bets ?? []).some((el: any) => codes.includes(String(el.code)))
        );
        return startRun ? Number(startRun.no) : null;
    };
    const firstSixStart = sixStartNo("35", "1");
    const secondSixStart = sixStartNo("36");
    const inSix = (start: number | null, no: number) => start !== null && no >= start && no < start + 6;
    hippodrome.firstSixStart = firstSixStart;
    hippodrome.secondSixStart = secondSixStart;

    const runsInOrder = [...hippodrome.runs].sort((a: any, b: any) => Number(a.no) - Number(b.no));
    let previousMinuteOfDay = -1;
    let dayOffset = 0;
    runsInOrder.forEach((run: any) => {
        const [hour, minute] = String(run.time).split(":").map(Number);
        const minuteOfDay = hour * 60 + minute;
        if (minuteOfDay < previousMinuteOfDay) {
            dayOffset += 1;
        }
        previousMinuteOfDay = minuteOfDay;

        run.running_date = new Date(hippodrome.date.split("T")[0] + "T" + run.time + ":00");
        run.running_date.setDate(run.running_date.getDate() + dayOffset);
        run.isFirstSix = inSix(firstSixStart, Number(run.no));
        run.isSecondSix = inSix(secondSixStart, Number(run.no));
    });
}

function registerHippodrome(hippodrome: any, todayKey: string) {
    console.info(`%c* Yeni Hipodrom Eklendi ${hippodrome.hippodrome}`, warningLogStyle);

    BulletinMap[hippodrome._id] = hippodrome;
    BulletinMapCardID[hippodrome.card_id] = BulletinMap[hippodrome._id];
    BulletinMap[hippodrome._id].runsMap = {};
    hippodrome.runs.forEach((run: any) => {
        BulletinMap[hippodrome._id].runsMap[Number(run.no)] = run;
        run.horseMap = {};
        run.horses.forEach((horse: any) => {
            run.horseMap[String(horse.no)] = horse;
        });
    });

    if (hippodrome.date.split("T")[0] !== todayKey) {
        hippodrome.wager_details.push({
            price: 1,
            runs: hippodrome.runs.map((r: any) => Number(r.no)),
            type: "0",
            wager: "Gelecek Program",
        });
    }

    DailyRaces.push(hippodrome);
}

function mergeSpecialEvents(specialEvents: any[]): boolean {
    let changed = false;
    const seenIds: any[] = [];

    for (const special of specialEvents) {
        if (!special?._id) {
            console.warn(`%c* Uzun vadeli yarış atlandı: _id yok`, warningLogStyle, special);
            continue;
        }
        // Kimliği olan kayıt "görüldü" sayılıyor; bozuk gövde yüzünden listeden DÜŞMESİN,
        // elimizdeki son sağlam kopya korunsun.
        seenIds.push(special._id);
        if (!special.event_odds) {
            console.warn(`%c* Uzun vadeli yarış atlandı: ${special._id}`, warningLogStyle, special);
            continue;
        }
        for (const [key, value] of Object.entries<any>(special.event_odds)) {
            value.no = Number(key);
        }

        const exSpecial: any = SpecialRaces.find((el: any) => el._id === special._id);
        if (!exSpecial) {
            SpecialRaces.push(special);
            changed = true;
            continue;
        }
        for (const [key, value] of Object.entries(special)) {
            if (!isSameValue(exSpecial[key], value)) {
                exSpecial[key] = value;
                changed = true;
            }
        }
    }

    for (let ix = SpecialRaces.length - 1; ix >= 0; ix -= 1) {
        if (!seenIds.includes((SpecialRaces[ix] as any)._id)) {
            SpecialRaces.splice(ix, 1);
            changed = true;
        }
    }

    return changed;
}

/** Sunucu saatine göre bugünün takvim günü: "YYYY-MM-DD". */
function serverTodayKey(): string {
    const today = ServerDate();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
}

/**
 * Kart listelerde görünür mü. Kart YALNIZ iki koşul BİRLİKTE sağlanınca düşer: bugüne ait
 * değil VE kapalı (`status` false). Biri yetmez - bugünün kapalı kartı gün boyu kalır
 * (koşuları sonuçlanmış olsa da sonuçlar oradan okunuyor), gece yarısını aşan yabancı kart
 * da açık kaldığı sürece kalır. Tarihi okunamayan kart ELENMEZ.
 */
function isVisibleCard(card: any, todayKey: string): boolean {
    return Boolean(card?.status) || String(card?.date ?? "").split("T")[0] === todayKey;
}

/** Kart listelerinin ortak kaynağı: kapanmış geçmiş kartlar ayıklanmış DailyRaces. */
function visibleDailyRaces(): any[] {
    const todayKey = serverTodayKey();
    return DailyRaces.filter((el: any) => isVisibleCard(el, todayKey));
}

function hippodromeSortKeys() {
    return [
        (e: any) => String(e.date).slice(0, 10),
        (e: any) => Number(e.foreign) * -1,
        (e: any) => {
            const opening = e.wager_details.length > 0 ? e.opening : "23:59:59";
            const parsed = new Date(`${String(e.date).split("T")[0]}T${opening}`);
            return Number.isNaN(parsed.getTime()) ? Infinity : parsed.getTime();
        },
        (e: any) => e.wager_details.length,
    ];
}

class BulletinProvider {
    init() {
        this.stop();
        this.call();
        this.start();
        this.getRaceCourses();
    }

    start() {
        bulletinTimer = setInterval(() => this.call(), 5 * 1000);
    }

    stop() {
        clearInterval(bulletinTimer);
        bulletinTimer = null;
        isFetchInFlight = false;
    }

    async call() {
        if (isFetchInFlight) return;
        isFetchInFlight = true;

        try {
            const response: any = await new ApiService().start("GET", `${bulletinUrlPrefix}/`, null, false);

            const todayKey = serverTodayKey();

            let hippodromesChanged = false;

            for (const hippodrome of response.data) {
                try {
                    prepareHippodrome(hippodrome);

                    const exHippodrome: any = DailyRaces.find((el: any) => el._id === hippodrome._id);
                    if (exHippodrome) {
                        if (mergeHippodrome(exHippodrome, hippodrome)) hippodromesChanged = true;
                        this.setNextRun(exHippodrome);
                    } else {
                        this.setNextRun(hippodrome);
                        registerHippodrome(hippodrome, todayKey);
                        hippodromesChanged = true;
                    }
                } catch (error) {
                    console.warn(
                        `%c* Kart atlandı: ${hippodrome?.keyword ?? hippodrome?._id ?? "?"}`,
                        warningLogStyle,
                        error
                    );
                }
            }

            const specialsChanged = mergeSpecialEvents(response.special_events ?? []);

            if (hippodromesChanged) {
                this.refreshAvailableHippodromes();
            }
            if (specialsChanged) {
                this.refreshAvailableLongtermHippodromes();
            }

            consecutiveFailures = 0;
        } catch (error) {
            consecutiveFailures += 1;
            console.error(
                `%c* Bülten güncellenemedi (üst üste ${consecutiveFailures}. hata)`,
                warningLogStyle,
                error
            );
        } finally {
            isFetchInFlight = false;
        }
    }

    setNextRun(hippodrom: Hippodrome) {
        try {
            hippodrom.nextRun = hippodrom.runs.find((r: any) => r.status === 0);
        } catch (e) {
            console.error(e);
            hippodrom.nextRun = null;
        }
    }

    publishSnapshot() {
        publishBulletinSnapshot({
            bulletinMap: BulletinMap,
            bulletinMapCardID: BulletinMapCardID,
            dailyRaces: visibleDailyRaces(),
            availableHippodromes,
            availableFobHippodromes,
            availableLongtermHippodromes,
        });
    }

    refreshAvailableHippodromes() {
        const ordered: any[] = _.orderBy(DailyRaces, hippodromeSortKeys(), ["asc", "asc", "asc", "desc"]);
        Object.assign(DailyRaces, ordered);

        const mutual = ordered.filter((e: any) =>
            e.wager_details.length > 0
            && e.status
            && e.wager_details.some((x: any) => {
                const bt = BettingTypesMap[Number(x.type)];
                return bt && !bt.isFob;
            }));
        availableHippodromes.splice(0, availableHippodromes.length, ...mutual);

        this.refreshAvailableFobHippodromes();
        this.publishSnapshot();
    }

    refreshAvailableFobHippodromes() {
        const ordered: any[] = _.orderBy(
            visibleDailyRaces().filter((el: any) => el.show_fob && el.wager_details.length > 0),
            hippodromeSortKeys(),
            ["asc", "asc", "asc", "desc"]
        );
        availableFobHippodromes.splice(0, availableFobHippodromes.length, ...ordered);
    }

    refreshAvailableLongtermHippodromes() {
        availableLongtermHippodromes.splice(0, availableLongtermHippodromes.length, ...SpecialRaces);
        this.publishSnapshot();
    }

    generateSubTypeLabel(bettingType: any, r: any) {
        const chunkSize = bettingType.subType;
        let chunk: any = [];
        for (let i = r; i < r + chunkSize; i += 1) {
            chunk.push(i);
        }
        return chunk.join("-");
    }

    getRaceCourses = async () => {
        try {
            const response: any = await new ApiService().start("GET", racecourseUrlPrefix, null, false);
            if (!response?.data || !Array.isArray(response.data) || response.data.length === 0) return [];
            const _hippodromes = response.data.map((el: any) => ({
                KEY: el._id,
                HIPODROM: el.name,
                YER: el.place,
                YABANCI: el.is_foreign,
            }));
            Object.assign(HippodromesMap, ..._hippodromes.map((x: any) => ({[String(x.KEY).toLowerCase()]: x})));
            publishHippodromesMap(HippodromesMap);
        } catch (error) {
            console.error("Failed to fetch race courses:", error);
            return [];
        }
    };
}

export const getPrettyFormatLastSix = (last6: any) => {
    let htmlResponse = "";
    if (last6 && (last6.indexOf("K") !== -1 || last6.indexOf("C") !== -1)) {
        const lastRuns = last6.match(/.{1,2}/g);
        for (let lR of lastRuns) {
            htmlResponse += `<span class="last-run-${lR[0]}">${lR[1]}</span>`;
        }
    } else {
        htmlResponse = last6;
    }
    return htmlResponse;
};

export const getPrettyFormatJewelry = (jewelry: any) => {
    let htmlResponse = "";
    if (jewelry) {
        const jewelries = jewelry.split(" ");
        for (let jewel of jewelries) {
            let textJewel = jewelryMapping[jewel];
            htmlResponse += `<span class="d-block mb-2"><b>${jewel}:</b> ${textJewel || jewel}</span>`;
        }
    }
    return htmlResponse;
};

export const bulletinProvider = new BulletinProvider();

export function buildMutualBettingTypes(hippodrome: any): any[] {
    if (!hippodrome?.wager_details) {
        return [];
    }
    const hippodromeBetTypes = hippodrome.wager_details.map((e: any) => Number(e.type));
    return bettingTypes
        .filter((el: any) => (
            hippodromeBetTypes.includes(el.id) && el.available === true && !el.isFob
        ))
        .map((el: any) => {
            const item: any = hippodrome.wager_details.find((x: any) => Number(x.type) === Number(el.id));
            const runs = Array.isArray(item.runs)
                ? item.runs
                : String(item.runs ?? "").split(",").filter((r: any) => r !== "").map((r: any) => Number(r));
            const copy: any = {...el, price: item.price ?? el.price, runs: runs};
            if (el.id === 0) {
                copy.column = runs.length;
                copy.subType = runs.length;
            }
            return copy;
        });
}

export function buildMutualBettingSubTypes(bettingType: any): any[] {
    if (!bettingType?.runs) {
        return [];
    }
    return bettingType.runs.map((r: any) => ({
        id: r,
        label: bulletinProvider.generateSubTypeLabel(bettingType, r),
    }));
}
